import type { JSX } from "preact";

import { Card, Empty, ErrorNote, Loading, Pill, Stat } from "../components/Ui";
import { ms, outcomeLabel, outcomeTone, percent, usd } from "../format";
import { useApi } from "../useApi";

interface ModelUsage {
  provider: string;
  model: string;
  calls: number;
  cost_usd: number;
  avg_latency_ms: number;
  failure_rate: number;
}

interface TaskUsage {
  task: string;
  calls: number;
  cost_usd: number;
  prompt_tokens: number;
  completion_tokens: number;
}

/** Mire ment a költség — a feladat belső neve helyett érthető megnevezés. */
export const TASK_LABEL: Record<string, { label: string; hint: string }> = {
  chat: { label: "Chat — látogatók", hint: "a honlapon feltett kérdések" },
  chat_eval: { label: "Chat — tesztmérés", hint: "a fejlesztő ellenőrző futásai" },
  chat_answer: { label: "Chat — modell-összehasonlítás", hint: "fejlesztői benchmark" },
  referral_extract: { label: "Beutaló felismerése", hint: "a feltöltött kép értelmezése" },
  referral_validate: { label: "Beutaló ellenőrzése", hint: "második, célzott olvasat" },
  referral_note: { label: "Plusz kérés a beutaló mellett", hint: "a kísérőüzenet olvasása" },
  price_match: { label: "Párosítás modellel", hint: "vizsgálatnév → árlista-tétel" },
  draft_compose: { label: "E-mail piszkozat", hint: "válaszlevél-tervezet" },
  clarify: { label: "Visszakérdezés", hint: "" },
  embedding: { label: "Tudásbázis indexelése", hint: "" },
};

interface Overview {
  days: number;
  referrals_total: number;
  referrals_by_outcome: { outcome: string; count: number }[];
  automatic_rate: number;
  human_review_rate: number;
  llm_calls: number;
  llm_cost_usd: number;
  llm_failure_rate: number;
  by_model: ModelUsage[];
  by_task?: TaskUsage[];
  active_rule_version: number | null;
  active_rule_items: number;
  active_rule_since: string | null;
}

export function OverviewView({ days }: { days: number }): JSX.Element {
  const { data, error, loading } = useApi<Overview>(`/admin/v1/overview?days=${days}`, [days]);

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (loading && !data) return <Loading />;
  if (!data) return <Empty>Nincs megjeleníthető adat.</Empty>;

  return (
    <>
      <div className="stats">
        <Stat label="Feldolgozott beutaló" value={String(data.referrals_total)} />
        <Stat
          label="Automatikus"
          value={percent(data.automatic_rate)}
          hint="emberi beavatkozás nélkül"
        />
        <Stat
          label="Emberhez irányítva"
          value={percent(data.human_review_rate)}
          hint="tervezett viselkedés, nem hiba"
        />
        <Stat label="Modellköltség" value={usd(data.llm_cost_usd)} hint={`${data.llm_calls} hívás`} />
      </div>

      <Card
        title="Kimenetel szerint"
        hint="A visszakérdezés és az emberi ellenőrzés nem hiba: a rendszer ilyenkor
              szándékosan nem vállalja a döntést."
      >
        {data.referrals_by_outcome.length === 0 ? (
          <Empty>Ebben az időszakban nem érkezett beutaló.</Empty>
        ) : (
          <table>
            <tbody>
              {data.referrals_by_outcome.map((row) => (
                <tr key={row.outcome}>
                  <td>
                    <Pill tone={outcomeTone(row.outcome)}>{outcomeLabel(row.outcome)}</Pill>
                  </td>
                  <td className="num">{row.count}</td>
                  <td className="num muted">
                    {percent(data.referrals_total ? row.count / data.referrals_total : 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card
        title="Mire ment a költség"
        hint="A chat és a beutaló-feldolgozás modellhasználata együtt. A chat adatai
              legfeljebb öt perc késéssel jelennek meg. Az összeg a szolgáltató
              listaárával számol; a számlán a kedvezmények miatt ennél kevesebb,
              a tudásbázis indexelése miatt néhány centtel több is lehet."
      >
        {(data.by_task ?? []).length === 0 ? (
          <Empty>Ebben az időszakban nem futott modellhívás.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Feladat</th>
                <th className="num">Hívás</th>
                <th className="num">Költség</th>
                <th className="num">Átlag / hívás</th>
              </tr>
            </thead>
            <tbody>
              {(data.by_task ?? []).map((t) => (
                <tr key={t.task}>
                  <td>
                    <strong>{TASK_LABEL[t.task]?.label ?? t.task}</strong>
                    {TASK_LABEL[t.task]?.hint && (
                      <span className="muted"> · {TASK_LABEL[t.task]?.hint}</span>
                    )}
                  </td>
                  <td className="num">{t.calls}</td>
                  <td className="num">{usd(t.cost_usd)}</td>
                  <td className="num muted">{usd(t.calls ? t.cost_usd / t.calls : 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Modellek" hint="Ugyanaz a költség modellenként.">
        {data.by_model.length === 0 ? (
          <Empty>Ebben az időszakban nem futott modellhívás.</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Modell</th>
                <th className="num">Hívás</th>
                <th className="num">Költség</th>
                <th className="num">Átlagos idő</th>
                <th className="num">Hibaarány</th>
              </tr>
            </thead>
            <tbody>
              {data.by_model.map((m) => (
                <tr key={`${m.provider}/${m.model}`}>
                  <td>
                    <strong>{m.model}</strong>
                    <span className="muted"> · {m.provider}</span>
                  </td>
                  <td className="num">{m.calls}</td>
                  <td className="num">{usd(m.cost_usd)}</td>
                  <td className="num">{ms(m.avg_latency_ms)}</td>
                  <td className="num">
                    {m.failure_rate > 0.05 ? (
                      <Pill tone="bad">{percent(m.failure_rate)}</Pill>
                    ) : (
                      percent(m.failure_rate)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Aktív szabálykészlet">
        {data.active_rule_version ? (
          <p>
            <strong>#{data.active_rule_version}</strong> — {data.active_rule_items} tétel
            {data.active_rule_since && (
              <span className="muted">
                {" "}
                · aktív {new Date(data.active_rule_since).toLocaleString("hu-HU")} óta
              </span>
            )}
          </p>
        ) : (
          <Empty>
            Nincs aktív szabálykészlet — az árazás nem működik. Indítson szinkront a
            Szabályok nézetben.
          </Empty>
        )}
      </Card>
    </>
  );
}
